import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { validateReviewLink, submitRecruiterEdit, certifyProfile, getReviewResumeUrl } from '../services/adminService';

// Step 10e Change 3: full profile review page — recruiter reads every section
// (headline, about, all role rewrites, pin_top_3) and leaves per-section comments
// and/or micro-edits. Comment section keys follow the admin routing convention:
//   'headline', 'about', 'experience_0'..'experience_N', 'skills' (legacy only).
// Certify at the bottom moves the order to stage='final_fix' (Ready to send).

const AccordionRow = ({ title, count, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-slate-800">{title}</span>
          {count != null && <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{count}</span>}
        </span>
        <svg className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {open && <div className="border-t border-slate-100 p-4">{children}</div>}
    </div>
  );
};

// Step 10f Fix 1: One clean "Edit or comment" toggle per section.
// - Rewritten text renders in place; the CARD content is what becomes editable.
// - Clicking "Edit or comment" swaps the read view for a textarea + comment box + Save/Cancel.
// - Save submits BOTH edited text (if changed) and comment (if non-empty) as a single record.
// - Each card owns its state — no overlap between cards.
// - Previously submitted feedback renders as a muted contained block above the card body,
//   with edit summary (not full duplicated text) + comment + expert reply.
const SectionFeedback = ({
  section, currentText, priorComments, sendComment,
  // Render function for the card BODY — the parent passes JSX for the read view.
  renderRead,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(currentText || '');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const openEdit = () => {
    setDraftText(currentText || '');
    setComment('');
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraftText(currentText || '');
    setComment('');
  };
  const save = async () => {
    const editedChanged = draftText.trim() !== (currentText || '').trim() && draftText.trim().length > 0;
    const commentTrimmed = comment.trim();
    if (!editedChanged && !commentTrimmed) { cancel(); return; }
    setSubmitting(true);
    try {
      await sendComment({
        section,
        comment: commentTrimmed || undefined,
        edited_content: editedChanged ? draftText.trim() : undefined,
        comment_type: editedChanged ? 'edit' : 'note',
      });
      setEditing(false);
      setDraftText('');
      setComment('');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Prior feedback — contained inside the card, muted, no full-text duplication */}
      {priorComments && priorComments.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {priorComments.map(c => (
            <div key={c.id} className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
              {c.edited_content && (
                <div className="text-green-800 mb-1">
                  ✎ <span className="font-medium">You suggested an edit</span>
                  <span className="text-slate-500"> ({c.edited_content.length} chars)</span>
                </div>
              )}
              {c.comment_text && (
                <div className="text-slate-700 mb-1">💬 {c.comment_text}</div>
              )}
              {c.expert_reply && (
                <div className="text-purple-800 bg-purple-50 border-l-2 border-purple-500 p-1.5 mt-1">
                  <span className="font-semibold">Expert:</span> {c.expert_reply}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Read view or in-place editor */}
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            rows={Math.min(20, Math.max(4, Math.ceil((draftText || '').length / 80)))}
            disabled={submitting}
            className="w-full text-sm text-slate-900 leading-relaxed p-3 border border-purple-300 bg-purple-50/30 rounded resize-y focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-60"
          />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Leave a note (optional)"
            rows={2}
            disabled={submitting}
            className="w-full text-xs p-2 border border-slate-200 rounded resize-y focus:outline-none focus:ring-1 focus:ring-purple-300 disabled:bg-slate-50"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={submitting}
              className="text-xs px-3 py-1 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-40"
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={submitting}
              className="text-xs text-slate-500 hover:text-slate-700 underline decoration-dotted disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {renderRead()}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={openEdit}
              className="text-xs text-purple-700 hover:text-purple-900 underline decoration-dotted"
            >
              ✎ Edit or comment
            </button>
            {justSaved && <span className="text-xs text-green-700">✓ Feedback saved</span>}
          </div>
        </>
      )}
    </>
  );
};

const RecruiterReview = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [certified, setCertified] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [reviewedChecked, setReviewedChecked] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeFileName, setResumeFileName] = useState(null);

  const load = async () => {
    try {
      const r = await validateReviewLink(token);
      if (!r.valid) { setError(r.message || 'This link has expired'); return; }
      setData(r);
      if (r.already_certified) setCertified(true);
      // Fetch resume signed URL (best-effort).
      try {
        const rr = await getReviewResumeUrl(token);
        if (rr?.resume_url) {
          setResumeUrl(rr.resume_url);
          setResumeFileName(rr.file_name || 'resume.pdf');
        }
      } catch (_) { /* non-fatal */ }
    } catch (_) {
      setError('This link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const sendComment = async (payload) => {
    await submitRecruiterEdit(token, payload);
    // Refresh so the "previous feedback" list updates immediately.
    await load();
  };

  const handleCertify = async () => {
    setCertifying(true);
    try {
      // sections_reviewed just records intent; backend already accepts free-form array.
      await certifyProfile(token, ['headline', 'about', 'experience']);
      setCertified(true);
      // Best-effort reload; if backend disables the link this may 404, which is fine.
      try { await load(); } catch (_) {}
    } catch (err) {
      alert(`Certify failed: ${err.message}`);
    } finally {
      setCertifying(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-slate-500">Loading review…</div></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">🔗</div>
          <div className="text-xl font-semibold text-slate-900 mb-2">Link expired</div>
          <div className="text-slate-500">{error}</div>
        </div>
      </div>
    );
  }
  if (certified) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-3xl">✓</div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">
            Certified — thank you{data?.recruiter?.name ? `, ${data.recruiter.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-slate-600">The Successlane team will finalize and deliver the profile to the candidate.</p>
        </div>
      </div>
    );
  }

  const {
    recruiter, candidate, profile, original_profile: originalProfile = {},
    hours_remaining, order_id, target_jds = [], intake_data = {}, comments = [],
  } = data;

  const roles = profile?.experience || [];
  const commentsBySection = comments.reduce((acc, c) => {
    const key = c.section || 'other';
    (acc[key] ||= []).push(c);
    return acc;
  }, {});
  const totalComments = comments.length;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-semibold">SL</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">Profile review & certification</div>
              <div className="text-[11px] text-slate-500 truncate">
                {candidate?.first_name || candidate?.name} · target: {candidate?.target_role}
              </div>
            </div>
          </div>
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{order_id}</span>
          {typeof hours_remaining === 'number' && (
            <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-medium">
              {hours_remaining}h left
            </span>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* Instructions */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-900">
          <div className="font-semibold mb-1">Hi {recruiter?.name?.split(' ')[0] || 'recruiter'} — one profile to review.</div>
          <div className="text-purple-800 leading-relaxed">
            Review each section below. Add comments or suggest edits where you'd change something.
            Then certify at the bottom. Zero-comment certifications are valid — if the profile is solid, just certify.
          </div>
        </div>

        {/* Context accordions */}
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Context</div>

          <AccordionRow title="Target role & companies">
            <div className="text-sm text-slate-700 space-y-1">
              <div><span className="text-slate-500">Role:</span> {candidate?.target_role || '—'}</div>
              <div><span className="text-slate-500">Industry:</span> {candidate?.target_industry || '—'}</div>
              {(candidate?.target_companies || []).length > 0 && (
                <div><span className="text-slate-500">Dream companies:</span>{' '}
                  {candidate.target_companies.join(', ')}
                </div>
              )}
            </div>
          </AccordionRow>

          <AccordionRow title="Resume" count={resumeFileName ? '1' : null}>
            {resumeUrl ? (() => {
              const nameLower = (resumeFileName || '').toLowerCase();
              const isPdf = nameLower.endsWith('.pdf');
              const isWord = nameLower.endsWith('.docx') || nameLower.endsWith('.doc');
              if (isWord) {
                return (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded border border-slate-200">
                    <div className="w-10 h-10 rounded bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-700 text-xs font-bold">DOC</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 truncate">{resumeFileName}</div>
                      <div className="text-xs text-slate-500">Download to read (Word docs can't preview inline)</div>
                    </div>
                    <a href={resumeUrl} download={resumeFileName || 'resume.docx'} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded bg-purple-600 text-white text-xs font-medium hover:bg-purple-700">
                      Download
                    </a>
                  </div>
                );
              }
              return (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-600 truncate">{resumeFileName}</span>
                    <a href={resumeUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-700 hover:underline">Open ↗</a>
                  </div>
                  {isPdf && <iframe src={resumeUrl} className="w-full rounded border border-slate-200" style={{ height: '520px' }} title="Resume PDF" />}
                </>
              );
            })() : (
              <div className="text-sm text-slate-400 italic">No resume uploaded.</div>
            )}
          </AccordionRow>

          <AccordionRow title={`Job descriptions (${target_jds.length})`} count={target_jds.length || null}>
            {target_jds.length === 0 ? (
              <div className="text-sm text-slate-400 italic">None supplied.</div>
            ) : (
              <div className="space-y-3">
                {target_jds.map((jd, i) => (
                  <div key={i} className="rounded border border-slate-200 p-3 bg-slate-50">
                    <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">JD {i + 1}</div>
                    {jd.source_url && (
                      <a href={jd.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-700 hover:underline block mb-1 truncate">
                        {jd.source_url}
                      </a>
                    )}
                    <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">
                      {jd.text || <em className="text-slate-400">No text</em>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </AccordionRow>

          <AccordionRow title="Intake answers" count={Object.keys(intake_data).length || null}>
            {Object.keys(intake_data).length === 0 ? (
              <div className="text-sm text-slate-400 italic">No intake data.</div>
            ) : (
              <div className="space-y-2">
                {Object.entries(intake_data).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
                    <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </AccordionRow>

          <AccordionRow title="Original LinkedIn profile">
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Headline</div>
                <div className="text-slate-700 whitespace-pre-wrap">
                  {originalProfile.headline || <em className="text-slate-400">Not available</em>}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">About</div>
                <div className="text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {originalProfile.about || <em className="text-slate-400">Not available</em>}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                  Experience ({(originalProfile.experience || []).length} roles)
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {(originalProfile.experience || []).map((r, i) => (
                    <div key={i} className="border-l-2 border-slate-200 pl-2">
                      <div className="text-xs font-semibold text-slate-800">
                        {r.job_title || r.title} · {r.company_name || r.company}
                      </div>
                      {(r.start_date || r.end_date) && (
                        <div className="text-[10px] text-slate-400">
                          {r.start_date}{r.start_date && (r.end_date || r.is_current) ? ' – ' : ''}
                          {r.is_current ? 'Present' : (r.end_date || '')}
                        </div>
                      )}
                      {r.description && (
                        <div className="text-xs text-slate-600 whitespace-pre-wrap mt-1">{r.description}</div>
                      )}
                    </div>
                  ))}
                  {(originalProfile.experience || []).length === 0 && (
                    <div className="text-xs text-slate-400 italic">Not available</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Skills</div>
                <div className="text-slate-700">
                  {(originalProfile.skills || []).length > 0
                    ? originalProfile.skills.join(', ')
                    : <em className="text-slate-400">Not available</em>}
                </div>
              </div>
            </div>
          </AccordionRow>
        </div>

        {/* Rewritten profile — review sections */}
        <div>
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Rewritten profile</div>

          {/* Headline */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
            <div className="text-xs font-semibold text-slate-800 uppercase tracking-wide mb-2">Headline</div>
            <SectionFeedback
              section="headline"
              currentText={profile?.headline || ''}
              priorComments={commentsBySection.headline || []}
              sendComment={sendComment}
              renderRead={() => (
                <div className="text-sm text-slate-900 leading-relaxed p-3 bg-slate-50 rounded border-l-2 border-purple-400 whitespace-pre-wrap">
                  {profile?.headline || <em className="text-slate-400">Not generated</em>}
                </div>
              )}
            />
          </div>

          {/* About */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
            <div className="text-xs font-semibold text-slate-800 uppercase tracking-wide mb-2">About</div>
            <SectionFeedback
              section="about"
              currentText={profile?.about || ''}
              priorComments={commentsBySection.about || []}
              sendComment={sendComment}
              renderRead={() => (
                <div className="text-sm text-slate-900 leading-relaxed p-3 bg-slate-50 rounded border-l-2 border-purple-400 whitespace-pre-wrap max-h-96 overflow-y-auto">
                  {profile?.about || <em className="text-slate-400">Not generated</em>}
                </div>
              )}
            />
          </div>

          {/* Role cards */}
          {roles.map((role, idx) => {
            const title = role.title || role.job_title || '(untitled)';
            const company = role.company || role.company_name || '';
            const dates = role.dates || (role.start_date ? `${role.start_date}${role.end_date || role.is_current ? ` – ${role.is_current ? 'Present' : role.end_date}` : ''}` : '');
            const sectionKey = `experience_${idx}`;
            // v1 joined-text format: paragraph + bullets (each prefixed with •) separated by \n.
            const seedText = [role.paragraph, ...(role.bullets || []).map(b => `• ${b}`)].filter(Boolean).join('\n');
            return (
              <div key={idx} className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-900">{title}</div>
                  {company && <div className="text-sm text-slate-500">· {company}</div>}
                  <div className="ml-auto text-xs text-slate-400">{dates}</div>
                </div>
                <SectionFeedback
                  section={sectionKey}
                  currentText={seedText}
                  priorComments={commentsBySection[sectionKey] || []}
                  sendComment={sendComment}
                  renderRead={() => (
                    <>
                      {role.paragraph && (
                        <p className="text-sm text-slate-700 leading-relaxed mb-2 p-3 bg-slate-50 rounded border-l-2 border-purple-400">
                          {role.paragraph}
                        </p>
                      )}
                      {(role.bullets || []).length > 0 && (
                        <ul className="space-y-1 mb-2 p-3 bg-slate-50 rounded border-l-2 border-purple-400">
                          {role.bullets.map((b, bi) => (
                            <li key={bi} className="text-sm text-slate-700 flex gap-2">
                              <span className="text-purple-400 flex-shrink-0">•</span>{b}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(role.skills || []).length > 0 && (
                        <div className="mt-2">
                          <div className="text-[10px] font-semibold text-purple-700 uppercase tracking-wide mb-1">Per-role skills</div>
                          <div className="flex flex-wrap gap-1.5">
                            {role.skills.map((s, si) => (
                              <span key={si} className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            );
          })}

          {/* Pin-top-3 (display only) */}
          {(() => {
            const pinned = (profile?.pin_top_3 || []).filter(Boolean).slice(0, 3);
            if (pinned.length === 0) return null;
            return (
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
                <div className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-2">
                  Pin these 3 as Top Skills on LinkedIn
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {pinned.map((s, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-300 text-xs font-medium">
                      📌 {s}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Certify block */}
        <div className="bg-white rounded-xl border-2 border-purple-300 p-5">
          <div className="text-sm font-semibold text-slate-900 mb-1">Certify this profile</div>
          <div className="text-xs text-slate-600 mb-3">
            {roles.length + 2} sections reviewed{totalComments > 0 && ` · ${totalComments} comment${totalComments === 1 ? '' : 's'} added`}.
            Zero-comment certifications are valid.
          </div>
          <label className="flex items-start gap-2 cursor-pointer mb-3 p-2 rounded border border-slate-200 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={reviewedChecked}
              onChange={(e) => setReviewedChecked(e.target.checked)}
              className="w-4 h-4 mt-0.5"
            />
            <span className="text-sm text-slate-700">
              I have reviewed the full profile including all experience sections.
            </span>
          </label>
          <button
            type="button"
            onClick={handleCertify}
            disabled={!reviewedChecked || certifying}
            className="w-full sm:w-auto px-5 py-2.5 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {certifying ? 'Certifying…' : '✓ Certify profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecruiterReview;
